import { useState } from 'react';
import { generatePath } from 'react-router-dom';
import { toast } from 'sonner';

import { rpc } from '…/app/common/rpc/client.mts';
import type { Workspace, WorkspaceId } from '…/app/state/states.d.ts';
import { Callout } from '…/app/common/Callout/Callout.tsx';

import { removeMeFromWorkspace } from '../queries/removeMeFromWorkspace.op.mts';

export function WorkspacesList({
  workspaces,
}: {
  workspaces: Map<WorkspaceId, Workspace>
}) {
  const [loadingIds, setLoadingIds] = useState<Set<WorkspaceId>>(new Set());

  const handleRefresh = async () => {
    await rpc.post('proxy/refresh-tokens', {
      json: { orb: true },
    });
  };

  const handleRemoveWorkspace = async (id: WorkspaceId) => {
    setLoadingIds(prev => new Set(prev).add(id));
    try {
      await removeMeFromWorkspace({ workspaceId: id });
      toast.success('Workspace removed successfully');
      // After successful removal, we could refresh the workspaces list or remove from the UI
      // This depends on how the app manages state
      await handleRefresh();
      // Refresh the window
      if (window) window.location.reload();
    } catch (error) {
      console.error('Failed to remove workspace:', error);
      toast.error('Failed to remove Workspace');
    } finally {
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // MapIterator
  const myWorkspaces: any[] = Array.from(workspaces.entries());
  const workspaceCount = myWorkspaces.length;

  // Determine grid columns based on workspace count
  let gridColumns = 'grid-cols-1';
  if (workspaceCount > 20) gridColumns = 'grid-cols-3';
  if (workspaceCount > 10) gridColumns = 'grid-cols-2';

  return (
    <div className="w-full p-6 bg-white rounded-lg shadow-sm">
      <h2 className="text-lg text-center font-medium text-gray-700 mb-4">Your Workspaces</h2>

      {workspaceCount > 15 && (
        <div className="mb-4">
          <Callout type="warn">
            <p className="text-sm">
              You have {workspaceCount} workspaces. Consider removing unused workspaces for better organization.
            </p>
          </Callout>
        </div>
      )}

      <ul className={`grid ${gridColumns} gap-3 w-full`} data-testid="list-of-workspace-links">
        {myWorkspaces.map(([id, workspace]) => (
          <li className="flex items-center justify-between bg-gray-50 p-0 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-all" key={id}>
            <a
              aria-label={`Go to ${workspace.name} workspace`}
              className="flex items-center justify-center p-3 border-0 bg-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              href={generatePath('/w/:workspaceId', { workspaceId: id })}
            >
              &gt;
            </a>

            <div className="pr-4 pl-2 py-3 flex-grow font-medium text-gray-800 flex flex-col">
              {workspace.name}

              <span className="block text-xs text-gray-400 mt-1">{workspace.id}</span>
            </div>

            <button
              aria-label={`Remove ${workspace.name} workspace`}
              className="flex items-center justify-center p-3 border-0 bg-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              disabled={loadingIds.has(id)}
              onClick={() => handleRemoveWorkspace(id)}
            >
              {loadingIds.has(id)
                ? (
                  <div className="w-5 h-5 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                ) : (
                  'x'
                )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

WorkspacesList.displayName = 'WorkspacesList';
